import { DeviceType } from '@/app/components/DeviceSelectionGallery/DeviceSelectionGallery.types'
import { LoadingAnimation, Text } from '@/components'
import UtilityProgramEnrollmentToggleContainer, {
  ProgramToggleOptions,
} from 'features/utility-programs/UtilityProgramEnrollmentManager/UtilityProgramEnrollmentToggleContainer/UtilityProgramEnrollmentToggleContainer'
import useDeviceEnrollments, {
  useDeviceEligibility,
} from '@/hooks/useDeviceEnrollments/useDeviceEnrollments'
import { match } from 'ts-pattern'
import { ComponentType, ReactNode } from 'react'

type Props = {
  deviceId: string | number
  deviceType: DeviceType
  options?: ProgramToggleOptions
  className?: string
  includeHeader?: boolean
  onUnenroll?: () => void
  wrapper?: ComponentType<{ children: ReactNode }>
}

export function useViewModel({ deviceId, deviceType }: Props) {
  const { deviceEnrollments, isLoading } = useDeviceEnrollments(
    deviceType,
    deviceId,
    { includeExpired: true },
  )

  return {
    deviceEnrollments,
    isLoading,
  }
}

function NoEnrollmentToggle(props: Props) {
  const { deviceEligibilities } = useDeviceEligibility(
    props.deviceType,
    props.deviceId,
  )

  const Wrapper = props.wrapper || (({ children }) => <>{children}</>)

  return deviceEligibilities?.map(
    ({ eligibility, viewConfig, utilityProgram }) => {
      if (!eligibility?.is_eligible || !viewConfig || !utilityProgram) {
        return null
      }

      return (
        <Wrapper>
          <UtilityProgramEnrollmentToggleContainer
            key={`enrollment_${eligibility.id}`}
            deviceEnrollment={null}
            viewConfig={viewConfig}
            utilityProgram={utilityProgram}
            options={props.options}
            onUnenroll={props.onUnenroll}
            deviceId={props.deviceId}
            deviceType={props.deviceType}
          />
        </Wrapper>
      )
    },
  )
}

export default function UtilityProgramEnrollmentManager(props: Props) {
  const { isLoading, deviceEnrollments } = useViewModel(props)
  const Wrapper = props.wrapper || (({ children }) => <>{children}</>)

  const loadingView = (
    <div className="flex flex-col gap-4">
      <Text variant="h3">Enrolled Programs</Text>
      <Text variant="body2">Checking for your enrolled programs...</Text>
      <LoadingAnimation type="falling" centered />
    </div>
  )

  const enrollmentComponents = deviceEnrollments
    ?.map((enrollmentData, i) => {
      if (
        !enrollmentData.enrollment ||
        !enrollmentData.utilityProgram ||
        !enrollmentData.viewConfig
      ) {
        return null
      }
      return (
        <UtilityProgramEnrollmentToggleContainer
          utilityProgram={enrollmentData.utilityProgram}
          deviceEnrollment={enrollmentData.enrollment}
          viewConfig={enrollmentData.viewConfig}
          options={props.options}
          onUnenroll={props.onUnenroll}
          deviceId={props.deviceId}
          deviceType={props.deviceType}
          key={`enrollment_${i}`}
        />
      )
    })
    .filter(Boolean)

  const loadedView = enrollmentComponents.map((component, i) => {
    return (
      <div className={i > 0 ? 'mt-4' : ''} key={`component_${i}`}>
        {component}
      </div>
    )
  })

  // hasDevices will take precedent over loading state due to the cache of enrollments
  // in thew viewModel
  const hasDevices = (deviceEnrollments?.length ?? 0) > 0

  const content = match({ isLoading, hasDevices })
    .with({ hasDevices: true }, () => loadedView)
    .with({ isLoading: true }, () => loadingView)
    .with({ isLoading: false }, () => null)
    .exhaustive()

  if (content == null) {
    return <NoEnrollmentToggle {...props} />
  }

  return (
    <Wrapper>
      <div className={props.className}>{content}</div>
    </Wrapper>
  )
}
